import React, { useState, ChangeEvent } from 'react';
import { FormGroup, Label, Input, Alert, Button } from 'reactstrap';
import axios from 'axios';

export interface ACHFormData {
  accountHolderName: string;
  accountHolderType: 'individual' | 'company';
  routingNumber: string;
  accountNumber: string;
}

interface ACHPaymentFormProps {
  onSubmit: (data: ACHFormData) => void;
  amount: number; // Amount in cents
}

const ACHPaymentForm: React.FC<ACHPaymentFormProps> = ({ onSubmit, amount }) => {
  const [formData, setFormData] = useState<ACHFormData>(() => {
    // Set initial form data based on environment
    const isDev = process.env.REACT_APP_ENVIRONMENT === 'DEV';
    console.log("isDev", isDev);
    return {
      accountHolderName: isDev ? 'Test Account' : '',
      accountHolderType: 'individual',
      routingNumber: isDev ? '110000000' : '',
      accountNumber: isDev ? '000123456789' : ''
    };
  });

  const [error, setError] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const processPayment = async () => {
    setError('');
    setIsProcessing(true);

    // Basic validation
    if (!formData.accountHolderName || !formData.routingNumber || !formData.accountNumber) {
      setError('All fields are required');
      setIsProcessing(false);
      return;
    }

    // Routing number validation
    if (formData.routingNumber.length !== 9) {
      setError('Routing number must be 9 digits');
      setIsProcessing(false);
      return;
    }

    // Account number validation
    if (formData.accountNumber.length < 4 || formData.accountNumber.length > 17) {
      setError('Account number must be between 4 and 17 digits');
      setIsProcessing(false);
      return;
    }

    try {
      const serverUrl = process.env.REACT_APP_SERVER || 'https://www.bidbro.com/api';
      const apiUrl = `${serverUrl}/process-ach-payment`;

      const response = await axios.post(apiUrl, {
        ...formData,
        amount // Pass the amount to the server
      });

      if (response.data.success) {
        setSuccess(true);
        onSubmit(formData);
      } else {
        setError(response.data.message || 'Payment processing failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred processing the payment');
    } finally {
      setIsProcessing(false);
    }
  };

  if (success) {
    return (
      <Alert color="success" className="text-center">
        Payment processed successfully!
      </Alert>
    );
  }

  return (
    <div className="p-4">
      {error && <Alert color="danger">{error}</Alert>}
      <div>
        <FormGroup>
          <Label for="accountHolderName">Account Holder Name</Label>
          <Input
            type="text"
            name="accountHolderName"
            id="accountHolderName"
            value={formData.accountHolderName}
            onChange={handleChange}
            placeholder="Enter account holder name"
            disabled={isProcessing}
          />
        </FormGroup>

        <FormGroup>
          <Label for="accountHolderType">Account Holder Type</Label>
          <Input
            type="select"
            name="accountHolderType"
            id="accountHolderType"
            value={formData.accountHolderType}
            onChange={handleChange}
            disabled={isProcessing}
          >
            <option value="individual">Individual</option>
            <option value="company">Company</option>
          </Input>
        </FormGroup>

        <FormGroup>
          <Label for="routingNumber">Routing Number</Label>
          <Input
            type="text"
            name="routingNumber"
            id="routingNumber"
            value={formData.routingNumber}
            onChange={handleChange}
            placeholder="Enter 9-digit routing number"
            maxLength={9}
            disabled={isProcessing}
          />
        </FormGroup>

        <FormGroup>
          <Label for="accountNumber">Account Number</Label>
          <Input
            type="text"
            name="accountNumber"
            id="accountNumber"
            value={formData.accountNumber}
            onChange={handleChange}
            placeholder="Enter account number"
            maxLength={17}
            disabled={isProcessing}
          />
        </FormGroup>

        <Button 
          type="button"
          color="primary"
          disabled={isProcessing}
          className="w-100"
          onClick={processPayment}
        >
          {isProcessing ? 'Processing...' : `Pay ${(amount / 100).toFixed(2)}`}
        </Button>

        <div className="text-muted mt-3">
          <small>
            By submitting this form, you authorize the payment and agree to the terms and conditions.
          </small>
        </div>
      </div>
    </div>
  );
};

export default ACHPaymentForm;